import { Divider, InputProps, Select, SelectProps } from 'antd';
import type { InputRef } from 'antd/es/input';
import type { BaseSelectRef } from 'rc-select/lib/BaseSelect';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DefaultFooter from './components/DefaultFooter';
import LoadingIndicator from './components/LoadingIndicator';
import SearchInput from './components/SearchInput';
import { DIVIDER_STYLE } from './constants';
import { useSearchSelect } from './hooks/useSearchSelect';
import { FooterRenderFn } from './types';

type SearchSelectV2Props = {
  inputSearchProps?: InputProps;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
  totalCount?: number;
  currentCount?: number;
  footerRender?: FooterRenderFn;
} & SelectProps;

function SearchSelectV2<T extends string>(props: SearchSelectV2Props) {
  const {
    inputSearchProps,
    onLoadMore,
    hasMore = false,
    loading = false,
    totalCount,
    currentCount,
    footerRender,
    onSearch,
    open: controlledOpen,
    onOpenChange: onOpenChangeProp,
    ...selectProps
  } = props;

  const { handleSearch, handleScroll, handleOpenChange } = useSearchSelect({
    onSearch,
    onLoadMore,
    hasMore,
    loading,
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchInputRef = useRef<InputRef | null>(null);
  const selectRef = useRef<BaseSelectRef | null>(null);
  const mergedOpen = typeof controlledOpen === 'boolean' ? controlledOpen : dropdownOpen;

  // NOTE: Ant Design steals focus when the dropdown opens (especially in multi-select mode
  // so we poke the search box a second time after the popup finishes animating
  const focusSearchInput = useCallback(() => {
    const attemptFocus = () => {
      selectRef.current?.blur?.();
      searchInputRef.current?.focus({ cursor: 'all' });
    };

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        attemptFocus();
        window.setTimeout(attemptFocus, 150);
      });
    } else {
      attemptFocus();
      setTimeout(attemptFocus, 150);
    }
  }, []);

  useEffect(() => {
    if (mergedOpen) {
      focusSearchInput();
    }
  }, [mergedOpen, focusSearchInput]);

  const footerProps = useMemo(
    () => ({
      currentCount,
      totalCount,
      hasMore,
      loading,
    }),
    [currentCount, totalCount, hasMore, loading]
  );

  return (
    <Select<T>
      {...selectProps}
      ref={selectRef}
      showSearch={false}
      filterOption={false}
      style={{ ...selectProps.style, width: '100%' }}
      onPopupScroll={handleScroll}
      popupRender={(menu) => (
        <div className="w-full relative ant-select-custom-dropdown">
          <SearchInput
            inputSearchProps={{ ...inputSearchProps, size: selectProps.size }}
            onSearch={handleSearch}
            dropdownOpen={mergedOpen}
            ref={searchInputRef}
          />
          <Divider style={DIVIDER_STYLE} />
          {menu}

          <LoadingIndicator loading={loading} />
          <Divider style={DIVIDER_STYLE} />

          {footerRender ? footerRender(footerProps) : <DefaultFooter {...footerProps} />}
        </div>
      )}
      onOpenChange={(open) => {
        handleOpenChange(open);
        if (typeof controlledOpen !== 'boolean') {
          setDropdownOpen(open);
        }
        onOpenChangeProp?.(open);

        if (open) {
          focusSearchInput();
        }
      }}
      open={controlledOpen}
    />
  );
}

SearchSelectV2.displayName = 'SearchSelectV2';

export default memo(SearchSelectV2);
